import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { formatQty } from '../lib/format.js';

const { t } = i18n;

interface ContentLine {
  id: string;
  remainingQty: string;
  sku: { code: string; nameLt: string; nameEn: string | null; type: string };
}

interface OutgoingMovement {
  id: string;
  type: string;
  qty: string;
  sourceOrderId: string | null;
  targetPallet: { id: string; code: string } | null;
  sourceOrder?: { id: string; code: string } | null;
  sku: { code: string; nameLt: string };
}

function destination(movement: OutgoingMovement): string {
  if (movement.targetPallet)
    return `${t('pallet.contents.to_pallet')}: ${movement.targetPallet.code}`;
  if (movement.sourceOrder) return `${t('order.pack.source_order')}: ${movement.sourceOrder.code}`;
  if (movement.type === 'TRANSFERRED' && !movement.sourceOrderId)
    return t('pallet.contents.to_shelf');
  return t(`pallet.contents.outgoing.${movement.type}`);
}

/** Read through the existing scoped detail endpoint when an inline preview opens. */
export function PalletContentsPanel({
  id,
  pallet,
}: {
  id: string;
  pallet: { id: string; code: string };
}) {
  const [lines, setLines] = useState<ContentLine[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [outgoing, setOutgoing] = useState<OutgoingMovement[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    const controller = new AbortController();
    setLines(null);
    setFailed(false);
    setOutgoing([]);
    api<{ contents: ContentLine[]; contentMovementsFrom?: OutgoingMovement[] }>(
      `/pallets/${pallet.id}`,
      { signal: controller.signal },
    )
      .then((data) => {
        if (!controller.signal.aborted) {
          setLines(data.contents.filter((line) => Number(line.remainingQty) > 0));
          setOutgoing(
            (data.contentMovementsFrom ?? []).filter(
              (m) =>
                ['TRANSFERRED', 'CONSUMED', 'ADJUSTED', 'DISPATCHED', 'LOST', 'PACKED'].includes(
                  m.type,
                ) && Number(m.qty) > 0,
            ),
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [pallet.id]);

  return (
    <div
      id={id}
      role="region"
      aria-label={`${t('pallet.contents.title')} · ${pallet.code}`}
      className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-left shadow-sm dark:border-emerald-800 dark:bg-emerald-950/30"
    >
      <div className="mb-2 flex items-baseline gap-2 text-sm font-semibold">
        <span>{t('pallet.contents.title')}</span>
        <span className="font-mono text-xs text-muted-foreground">{pallet.code}</span>
      </div>
      {failed ? (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {t('common.error')}
        </p>
      ) : lines === null ? (
        <p role="status" className="text-sm">
          {t('common.loading')}
        </p>
      ) : lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('pallet.contents.preview_empty')}</p>
      ) : (
        <ul className="grid gap-2 text-sm sm:grid-cols-2 xl:grid-cols-3">
          {lines.map((line) => (
            <li
              key={line.id}
              className="flex items-start justify-between gap-3 rounded border border-border bg-background p-2"
            >
              <div className="min-w-0 break-words">
                <div className="font-mono text-xs break-all">{line.sku.code}</div>
                <div>
                  {user?.locale === 'en' ? (line.sku.nameEn ?? line.sku.nameLt) : line.sku.nameLt}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(`sku.type.${line.sku.type}`)}
                </div>
              </div>
              <div className="shrink-0 text-right tabular-nums">
                <div className="text-xs text-muted-foreground">
                  {t('pallet.contents.remaining')}
                </div>
                {formatQty(Number(line.remainingQty))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!failed && lines !== null && outgoing.length > 0 && (
        <section
          className="mt-3 border-t border-border pt-2 text-sm text-muted-foreground"
          aria-label={t('pallet.contents.outgoing_title')}
        >
          <h4 className="mb-1 font-medium">{t('pallet.contents.outgoing_title')}</h4>
          <ul className="space-y-1">
            {outgoing.map((movement) => (
              <li key={movement.id}>
                <span className="font-mono">{movement.sku.code}</span> {movement.sku.nameLt} —{' '}
                {formatQty(Number(movement.qty))} → {destination(movement)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
